<template>
  <v-sheet class="list-users">
    <div class="list-users-title">
      <div>
        {{ $t('views.userAdmin.list') }}
      </div>
      <div>
        <v-btn class="mr-4" color="primary" @click="inviteUserDialog = true">{{ $t('common.actions.inviteUser') }}</v-btn>
        <v-btn icon @click="doReload">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn icon @click="showUserAdminPanel = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>
    <div class="list-users-table">
      <ZDataTable ref="zList" :items="list" :headers="headers" item-key="id" :loading="loading">
        <template #item.display.actions="{ item }">
          <div class="table-actions">
            <v-btn icon="mdi-pencil" variant="text" @click="selectedUser = item"></v-btn>
            <v-btn icon="mdi-delete" variant="text" @click="del(item)"></v-btn>
          </div>
        </template>
      </ZDataTable>
    </div>
  </v-sheet>
  <v-dialog v-model="editProfile" width="50vw">
    <EditUser :user="selectedUser" @cancel="selectedUser = undefined" @save="saveUser" @close="editProfile = false"></EditUser>
  </v-dialog>
  <v-dialog v-model="inviteUserDialog" width="30vw">
    <InviteUser v-model:email-error="inviteEmailError" :loading="loading" @cancel="inviteUserDialog = false" @save="invite" />
  </v-dialog>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue'
import { computed, ref, onMounted, watch } from 'vue'

import { storeToRefs } from 'pinia'

import { HTTPError } from 'ky'
import { useI18n } from 'vue-i18n'

import { useNotifications, ZDataTable } from '@tsyl/zapan2'
import type { Header, ItemListOptions, ItemsFn } from '@tsyl/zapan2/lib/types/ZDataTable'

import type { Role, User } from '@/types/User'

import { deleteUser, editUser, inviteUser, listUsers, updateUserRoles, listAvailableUserRoles } from '../../services/api/user'
import AuthStore from '../../stores/auth'

import EditUser from './EditUser.vue'
import InviteUser from './InviteUser.vue'

const { addNotification } = useNotifications()
const { t } = useI18n()

const authStore = AuthStore()
const { showUserAdminPanel, user } = storeToRefs(authStore)

// Refs
const inviteUserDialog = ref(false)
const inviteEmailError = ref('')
const selectedUser = ref<User>()
const zList = ref()
const loading = ref(false)
const availableRoles = ref<Role[]>([])

watch(inviteUserDialog, (open) => {
  if (open) inviteEmailError.value = ''
})

onMounted(async () => {
  try {
    availableRoles.value = (await listAvailableUserRoles()) as Role[]
  } catch (error) {
    console.error('Error fetching roles:', error)
  }
})

const editProfile = computed({
  get() {
    return !!selectedUser.value
  },
  set(value: boolean) {
    if (!value) selectedUser.value = undefined
  }
})

const list: ItemsFn<User> = async (options: ItemListOptions) => {
  const users = await listUsers({
    limit: options.limit,
    offset: options.offset
  })
  return {
    items: users.users,
    total: users.total
  }
}

function doReload() {
  zList.value?.doReload()
}

const del = async (user: User) => {
  if (confirm(t('messages.info.confirmDeleteUser'))) {
    try {
      loading.value = true
      await deleteUser(user)
      addNotification({
        type: 'success',
        title: t('messages.success.userDeleted')
      })
      doReload()
    } catch (e) {
      console.error('Delete user failed', e)
      addNotification({
        type: 'error',
        title: t('messages.error.userDelete')
      })
    } finally {
      loading.value = false
    }
  }
}

const saveUser = async (userToEdit: User) => {
  if (!currentUserEditSafetyCheck(userToEdit, user)) {
    selectedUser.value = undefined
    return
  }
  try {
    loading.value = true
    selectedUser.value = undefined
    inviteUserDialog.value = false
    await editUser(userToEdit)
    if (userToEdit.roles && userToEdit.user_id) {
      await updateUserRoles(userToEdit.user_id, userToEdit.roles)
    }
    addNotification({
      type: 'success',
      title: t('messages.success.userEdited')
    })
    doReload()
  } catch (e) {
    console.error('Edit user failed', e)
    addNotification({
      type: 'error',
      title: t('messages.error.userEdit')
    })
  } finally {
    loading.value = false
  }
}

function currentUserEditSafetyCheck(userToEdit: User, currentUser: Ref<User | null>) {
  const administratorRole = availableRoles.value.find((role) => role.name === 'administrator')
  if (userToEdit.user_id === currentUser.value?.id) {
    if (currentUser.value?.roles?.includes('administrator') && !(userToEdit.roles ?? []).includes(administratorRole?.id ?? '')) {
      const confirmMessage = t('messages.info.confirmRemoveAdminRole')
      if (!confirm(confirmMessage)) return false
    }
  }
  return true
}

const invite = async (user: User) => {
  try {
    loading.value = true
    inviteEmailError.value = ''
    await inviteUser(user)
    inviteUserDialog.value = false
    addNotification({
      type: 'success',
      title: t('messages.success.userInvited')
    })
    doReload()
  } catch (e) {
    if (e instanceof HTTPError && e.response.status === 409) {
      let body: { code?: string } | null = null
      try {
        body = await e.response.json()
      } catch {
        body = null
      }
      if (body?.code === 'USER_ALREADY_REGISTERED') {
        inviteEmailError.value = t('messages.validation.emailAlreadyRegistered')
        addNotification({
          type: 'error',
          title: t('messages.error.userAlreadyRegistered')
        })
        return
      }
    }
    addNotification({
      type: 'error',
      title: t('messages.error.userInvite')
    })
  } finally {
    loading.value = false
  }
}

const headers = computed(
  () =>
    [
      {
        text: t('domain.fields.username'),
        value: 'nickname'
      },
      {
        text: t('domain.fields.email'),
        value: 'email'
      },
      {
        text: t('domain.fields.provider'),
        value: 'user_id',
        transform(value: string, item: User) {
          return item.identities?.[0]?.provider || 'local'
        }
      },
      {
        text: t('domain.fields.lastName'),
        value: 'family_name',
        transform(value: string, item: User) {
          return item.user_metadata?.family_name || value
        }
      },
      {
        text: t('domain.fields.firstName'),
        value: 'given_name',
        transform(value: string, item: User) {
          return item.user_metadata?.given_name || value
        }
      },
      {
        text: t('common.concepts.actions'),
        value: 'actions',
        fixed: true
      }
    ] as Header[]
)
</script>

<style scoped lang="scss">
.list-users {
  width: calc(100vw - 56px);
  height: 100vh;
  position: absolute;
  z-index: 6;
  left: 56px;
  top: 0;
  overflow: hidden !important;
  &-title {
    padding: 8px 10px;
    display: flex;
    font-size: 28px;
    align-items: center;
    justify-content: space-between;
  }
  &-table {
    width: 100%;
    height: 90vh;
  }
}

.table-actions {
  display: flex;
  flex-direction: row;

  justify-content: center;
}

:deep(.v-overlay__content) {
  display: flex !important;
  flex-direction: row !important;
  justify-content: center !important;
}
</style>
